import {Controller} from "@hotwired/stimulus"
import {useZoomListener} from "./mixins/use_zoom_listener.js"
import {useResizeListener} from "./mixins/use_resize_listener";
import {getElementTotalHeight} from "../helpers/element_dimensions_tool"
import {debounce} from "lodash";

/**
 * Left panel controller
 * - allows the sidebar on the left to toggle it's class depending on viewport.
 * - respects user setting stored in local storage
 *
 * usage:
 * %div{data: {controller: 'left-panel'}}  #controller element
 *
 *  %button{data: {action: 'click->left-panel#userClose'}}
 *
 *  %button{data: {action: 'click->left-panel#userOpen'}}
 */

export default class extends Controller {
  static targets = ['toggleTarget', 'toggler', 'toolbar', 'scrollableContent', 'togglersGroup', 'searchResults']

  storageUserKey = "sidebar-state"
  collapsedClass = "collapsed"
  breakpointValue = 1200

  mainMenuElement = null
  mainMenuElementHeight = 0

  connect() {
    super.connect()
    useZoomListener(this);
    useResizeListener(this);

    this.mainMenuElement = document.querySelector('[data-left-panel-external-target="mainMenu"]');

    this._getMainMenuElementHeight();
    this._setLeftPanelHeight();
    this._setMainMenuBottom();
    this.initFromLocalStorage();

    this.mainMenuPositionHandler = debounce(this._watchMainMenuPosition.bind(this), 10);
    window.addEventListener('scroll', this.mainMenuPositionHandler);

    this.leftPanelHeightHandler = this._setLeftPanelHeight.bind(this);
    window.addEventListener('collapse:search', this.leftPanelHeightHandler);
  }

  disconnect() {
    super.disconnect();
    window.removeEventListener('scroll', this.mainMenuPositionHandler);
    window.removeEventListener('collapse:search', this.leftPanelHeightHandler);
  }

  _watchMainMenuPosition() {
    this._setMainMenuBottom();
    this._setLeftPanelHeight();
  }

  _setLeftPanelHeight() {
    const footerElement = document.querySelector('[data-left-panel-external-target="footer"]');

    this._getMainMenuElementHeight();
    const footerHeight = getElementTotalHeight(footerElement);

    const desiredHeight = window.visualViewport.height
      - footerHeight
      - this.mainMenuBottom
      - this._getTogglerTargetsHeight()
      - 1;

    this.element.style.height = `${desiredHeight}px`;


    setTimeout(() => {
      const scrollableHeight = desiredHeight
        - getElementTotalHeight(this.toolbarTarget)
        - this.searchResultsTarget.getBoundingClientRect().height;
      this.scrollableContentTarget.style.height = `${scrollableHeight}px`;
    });
  }

  _getTogglerTargetsHeight() {
    return this.togglersGroupTarget.getBoundingClientRect().height;
  }

  _getMainMenuElementHeight() {
    this.mainMenuElementHeight = getElementTotalHeight(this.mainMenuElement);
  }

  _setMainMenuBottom() {
    this.mainMenuBottom = this.mainMenuElement.getBoundingClientRect().bottom;
    const style = getComputedStyle(this.mainMenuElement);
    const marginBottom = parseFloat(style.marginBottom) || 0;
    this.mainMenuBottom += marginBottom
    this.mainMenuBottom = Math.max(0, this.mainMenuBottom)
  }

  initFromLocalStorage() {
    const storedClass = localStorage.getItem(this.storageUserKey)
    if (storedClass) {
      this.restoreFromStorage()
    }
    this.responsiveChange()
  }

  onZoomChange() {
    this.responsiveChange();
  }

  onResizeChange() {
    this.responsiveChange();
  }

  restoreFromStorage() {
    const state = localStorage.getItem(this.storageUserKey)
    if (state === "open") {
      this.userOpen()
    } else {
      this.userClose()
    }
  }

  responsiveChange() {
    if (this.breakpointValue < window.innerWidth) {
      this.responsiveOpen()
    } else {
      this.responsiveClose()
    }
    this._setLeftPanelHeight();
    this.element.classList.remove('invisible');
  }

  // always hide the panel for smaller viewport
  responsiveClose() {
    this.element.classList.add(this.collapsedClass)
    this.report("closed")
  }

  // show the panel if user set it to open or user setting does not exist
  responsiveOpen() {
    const storedClass = localStorage.getItem(this.storageUserKey)
    if (!storedClass || storedClass === "open") {
      this.element.classList.remove(this.collapsedClass)
      this.report("open")
    }
  }

  userClose() {
    this.element.classList.add(this.collapsedClass)
    localStorage.setItem(this.storageUserKey, "closed")
    this.report("closed")
    this._setLeftPanelHeight()
  }

  userOpen() {
    this.element.classList.remove(this.collapsedClass)
    localStorage.setItem(this.storageUserKey, "open")
    this.report("open")
    this._setLeftPanelHeight()
  }

  report(state) {
    const changeEvent = new CustomEvent('left-panel-change', {detail: {state: state}, bubbles: true})
    this.element.dispatchEvent(changeEvent)
  }
}